<div class="flex flex-col h-full">
  <div aria-live="polite" aria-atomic="true" class="sr-only">
    {{ 'common.dialogs.selectMeasures.totalCount' | transloco: { total: totalRecords(), loaded: measures().length } }}
  </div>

  @if (multiselect() && selectedCount() > 0) {
    <div class="flex items-center gap-(--space-2) pb-(--space-2)">
      <p-checkbox
        [binary]="true"
        [ngModel]="showSelectedOnly()"
        (ngModelChange)="toggleShowSelectedOnly($event)"
        inputId="show-selected-only"
        [attr.aria-label]="'common.dialogs.selectMeasures.showSelectedOnly' | transloco: { count: selectedCount() }" />
      <label for="show-selected-only" class="cursor-pointer text-sm">
        {{ 'common.dialogs.selectMeasures.showSelectedOnly' | transloco: { count: selectedCount() } }}
      </label>
    </div>
  }

  <div class="flex-1 overflow-hidden">
    <p-table
      #measuresTable
      [value]="displayData()"
      styleClass="p-datatable-sm w-full"
      [tableStyle]="{ 'table-layout': 'fixed' }"
      [rowTrackBy]="trackByMeasureId"
      dataKey="id"
      [scrollable]="true"
      scrollHeight="flex"
      [lazy]="true"
      (onLazyLoad)="onLazyLoad($event)"
      [totalRecords]="totalRecords()"
      [rows]="PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE"
      [paginator]="true"
      [rowsPerPageOptions]="PAGINATION_CONSTANTS.PAGE_SIZE_OPTIONS"
      [paginatorDropdownAppendTo]="'body'"
      role="grid"
      [attr.aria-label]="tableAriaLabel"
      [attr.aria-busy]="isLoading()">
      <ng-template #header>
        <tr role="row">
          <th scope="col" style="width: 3rem" role="columnheader">
            <span class="sr-only">{{ 'common.dialogs.selectMeasures.selection' | transloco }}</span>
          </th>
          @for (column of sortableColumns; track column.field) {
            <th scope="col" [style.width]="column.width" role="columnheader" [pSortableColumn]="column.field">
              <div class="flex items-center">
                {{ column.labelKey | transloco }}
                @if (measuresTable.sortField === column.field) {
                  <ces-icon aria-hidden="true">{{ measuresTable.sortOrder === -1 ? 'south' : 'north' }}</ces-icon>
                } @else {
                  <ces-icon aria-hidden="true" rotate="90">sync_alt</ces-icon>
                }
              </div>
            </th>
          }
        </tr>
        <tr role="row">
          <th scope="col" role="columnheader">
            @if (multiselect()) {
              <p-checkbox
                [binary]="true"
                [ngModel]="allVisibleSelected()"
                [indeterminate]="someVisibleSelected() && !allVisibleSelected()"
                (ngModelChange)="toggleAllVisible($event)"
                [attr.aria-label]="'common.dialogs.selectMeasures.selectAllVisible' | transloco" />
            }
          </th>
          <th scope="col" role="columnheader">
            <input
              pInputText
              [formControl]="nomControl"
              class="w-full text-xs"
              [attr.aria-label]="'common.dialogs.selectMeasures.filters.byName' | transloco" />
          </th>
          <th scope="col" role="columnheader">
            <p-multiselect
              [options]="typeMesureOptions()"
              [filter]="typeMesureOptions().length >= 10"
              [formControl]="typeMesureControl"
              optionLabel="label"
              optionValue="value"
              [showClear]="true"
              appendTo="body"
              [maxSelectedLabels]="1"
              [selectedItemsLabel]="'common.dialogs.selectMeasures.filters.selectedItems' | transloco"
              [attr.aria-label]="'common.dialogs.selectMeasures.filters.byType' | transloco"
              styleClass="w-full text-xs" />
          </th>
          <th scope="col" role="columnheader">
            <p-multiselect
              [options]="genreOptions()"
              [formControl]="genreControl"
              optionLabel="label"
              optionValue="value"
              [showClear]="true"
              appendTo="body"
              [maxSelectedLabels]="1"
              [selectedItemsLabel]="'common.dialogs.selectMeasures.filters.selectedItems' | transloco"
              [attr.aria-label]="'common.dialogs.selectMeasures.filters.byGenre' | transloco"
              styleClass="w-full text-xs" />
          </th>
          <th scope="col" role="columnheader">
            <p-multiselect
              [options]="localisationOptions()"
              [filter]="localisationOptions().length >= 10"
              [formControl]="localisationControl"
              optionLabel="label"
              optionValue="value"
              [showClear]="true"
              appendTo="body"
              [maxSelectedLabels]="1"
              [selectedItemsLabel]="'common.dialogs.selectMeasures.filters.selectedItems' | transloco"
              [attr.aria-label]="'common.dialogs.selectMeasures.filters.byLocation' | transloco"
              styleClass="w-full text-xs" />
          </th>
          <th scope="col" role="columnheader">
            <p-select
              [options]="uniteOptions()"
              [filter]="uniteOptions().length >= 10"
              [formControl]="uniteControl"
              optionLabel="label"
              optionValue="value"
              [showClear]="true"
              appendTo="body"
              [attr.aria-label]="'common.dialogs.selectMeasures.filters.byUnit' | transloco"
              styleClass="w-full text-xs" />
          </th>
          <th scope="col" role="columnheader">
            <input
              pInputText
              [formControl]="descriptionControl"
              class="w-full text-xs"
              [attr.aria-label]="'common.dialogs.selectMeasures.filters.byDescription' | transloco" />
          </th>
        </tr>
      </ng-template>
      <ng-template #body let-measure let-rowIndex="rowIndex">
        @if (measure.isPlaceholder) {
          <tr [style.opacity]="1 - rowIndex * 0.1" role="row">
            <td role="gridcell"><p-skeleton width="1.5rem" height="1.5rem" /></td>
            <td role="gridcell"><p-skeleton height="1rem" /></td>
            <td role="gridcell"><p-skeleton height="1rem" /></td>
            <td role="gridcell"><p-skeleton height="1rem" /></td>
            <td role="gridcell"><p-skeleton height="1rem" /></td>
            <td role="gridcell"><p-skeleton height="1rem" /></td>
            <td role="gridcell"><p-skeleton height="1rem" /></td>
          </tr>
        } @else {
          <tr [class.bg-surface-50]="measure.selected" role="row" [attr.aria-selected]="measure.selected">
            <td role="gridcell">
              @if (multiselect()) {
                <p-checkbox
                  [binary]="true"
                  [ngModel]="measure.selected"
                  (ngModelChange)="toggleMeasure(measure.id, $event)"
                  [inputId]="'measure-' + measure.id"
                  [attr.aria-label]="'common.dialogs.selectMeasures.selectMeasure' | transloco: { name: measure.name }" />
              } @else {
                <p-radioButton
                  [value]="measure"
                  [ngModel]="selectedMeasure()"
                  (ngModelChange)="selectedMeasure.set($event)"
                  [inputId]="'measure-' + measure.id"
                  [attr.aria-label]="'common.dialogs.selectMeasures.selectMeasure' | transloco: { name: measure.name }" />
              }
            </td>
            <td role="gridcell">
              <div class="flex items-center gap-1">
                @if (measure.suspended) {
                  <ces-suspended-measure-icon [tooltip]="'entities.measure.suspendedTooltip' | transloco" />
                }
                <label [for]="'measure-' + measure.id" class="cursor-pointer font-medium">
                  {{ measure.name }}
                </label>
              </div>
            </td>
            <td role="gridcell">{{ measure.type || '-' }}</td>
            <td role="gridcell">{{ measure.genre || '-' }}</td>
            <td role="gridcell">{{ measure.localisation || '-' }}</td>
            <td role="gridcell">{{ measure.unit || '-' }}</td>
            <td role="gridcell">
              <div class="description-cell" [title]="measure.description || ''">{{ measure.description || '-' }}</div>
            </td>
          </tr>
        }
      </ng-template>
      <ng-template #emptymessage>
        <tr>
          <td colspan="7" class="text-center py-8 text-(color:--text-secondary)">
            {{ 'common.messages.noMeasuresMatchFilters' | transloco }}
          </td>
        </tr>
      </ng-template>
    </p-table>
  </div>

  <ces-dialog-footer
    [submitLabel]="'common.buttons.save' | transloco"
    [submitDisabled]="saving()"
    (onCancel)="cancel()"
    (onSubmit)="save()" />
</div>
